import type { EchoflicksSessionJourney } from "../types";

type Document = Record<string, unknown>;

function document(value: unknown): Document | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Document)
    : null;
}

/** Preserve evidence as reported; a planned reuse count never proves actual reuse. */
export function buildRepromptEvidence(journey?: EchoflicksSessionJourney) {
  const nodes = [...(journey?.nodes ?? [])].sort((a, b) =>
    (a.occurred_at ?? "").localeCompare(b.occurred_at ?? ""),
  );
  let plan: Document | null = null;
  let beats: Document | null = null;
  const unresolvedSubmissions = new Set<string>();
  for (const node of nodes) {
    const raw = document(node.raw_payload);
    const payload = document(raw?.payload) ?? raw;
    if (!payload) continue;
    if (
      node.type === "refinement.plan_committed" &&
      ["refinement-impact-plan.v1", "refinement-impact-plan.v2"].includes(
        String(payload.schema_version),
      )
    ) {
      plan = payload;
    }
    if (
      ["refinement.beat_sync_verified", "refinement.beat_sync_failed"].includes(
        node.type,
      ) &&
      payload.schema_version === "encoded-beat-sync-verification.v1"
    ) {
      beats = payload;
    }
    if (node.type === "edit.submission_outcome_unknown") {
      unresolvedSubmissions.add(
        typeof payload.event_id === "string" ? payload.event_id : node.id,
      );
    }
    if (
      node.type === "edit.submission_reconciled" &&
      typeof payload.event_id === "string" &&
      ["published", "settled"].includes(String(payload.outcome))
    ) {
      unresolvedSubmissions.delete(payload.event_id);
    }
  }
  // A foreign/mismatched receipt must not look like verification of this plan.
  if (
    plan &&
    beats &&
    (plan.workload_id !== beats.workload_id ||
      plan.config_digest !== beats.config_digest)
  ) {
    beats = null;
  }
  return { plan, beats, outcomeUnknown: unresolvedSubmissions.size > 0 };
}
